'use client';

import React from 'react';
import Image from 'next/image';
import { Twitter, Linkedin, Mail } from '@/lib/icons';

interface AuthorBioProps {
  author: string;
  authorImage?: string;
  authorBio?: string;
  authorTwitter?: string;
  authorLinkedIn?: string;
  authorEmail?: string;
  /** Real title for known authors (e.g. "Co-Founder & CEO, LeadPops"); guests fall back to "Content Contributor". */
  authorTitle?: string;
  /** On-site author page — the entity hub every byline links to (e.g. /author/andrew-pawlak). */
  authorPage?: string;
}

export function AuthorBio({
  author,
  authorImage,
  authorBio,
  authorTwitter,
  authorLinkedIn,
  authorEmail,
  authorTitle,
  authorPage
}: AuthorBioProps) {
  // Default bio if none provided
  const bio = authorBio || `${author} is part of the LeadPops content team, helping mortgage professionals generate exclusive leads through proven digital marketing strategies.`;
  
  // Default image if none provided
  const image = authorImage || '/images/team/leadpops-team.jpg';

  return (
    <div className="bg-gray-50 rounded-xl p-6 sm:p-8">
      <div className="flex flex-col sm:flex-row gap-6">
        {/* Author Image */}
        <div className="flex-shrink-0">
          <div className="w-24 h-24 sm:w-32 sm:h-32 relative rounded-full overflow-hidden border-4 border-white shadow-lg">
            <Image
              src={image}
              alt={author}
              fill
              className="object-cover"
              sizes="(max-width: 640px) 96px, 128px"
            />
          </div>
        </div>

        {/* Author Info */}
        <div className="flex-1">
          <div className="mb-3">
            <h3 className="text-xl font-bold text-gray-900 mb-1">
              {authorPage ? (
                <a href={authorPage} className="hover:underline">
                  About {author}
                </a>
              ) : (
                `About ${author}`
              )}
            </h3>
            <p className="text-sm text-primary font-medium">{authorTitle || 'Content Contributor'}</p>
          </div>
          
          <p className="text-gray-700 leading-relaxed mb-4">
            {bio}
          </p>

          {/* Social Links */}
          <div className="flex items-center gap-3">
            {authorTwitter && (
              <a 
                href={authorTwitter}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-9 h-9 bg-white border border-gray-300 rounded-full hover:bg-primary hover:text-white hover:border-primary transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="w-4 h-4" />
              </a>
            )}
            {authorLinkedIn && (
              <a 
                href={authorLinkedIn}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-9 h-9 bg-white border border-gray-300 rounded-full hover:bg-primary hover:text-white hover:border-primary transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            )}
            {(authorEmail || !authorTwitter && !authorLinkedIn) && (
              <a 
                href={`mailto:${authorEmail || 'support@rebeliq.com'}`}
                className="inline-flex items-center justify-center w-9 h-9 bg-white border border-gray-300 rounded-full hover:bg-primary hover:text-white hover:border-primary transition-colors"
                aria-label="Email"
              >
                <Mail className="w-4 h-4" />
              </a>
            )}
            
            {/* View More Articles Link */}
            <a
              href={`/blog?author=${encodeURIComponent(author)}`}
              className="ml-auto text-sm font-medium text-primary hover:text-primary-dark transition-colors"
            >
              View all articles by {author.split(' ')[0]} →
            </a>
            {authorPage && (
              <a
                href={authorPage}
                className="text-sm font-medium text-gray-600 hover:text-primary transition-colors"
              >
                Full bio
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}